import { useTranslation } from "react-i18next";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useAlertContext } from "../../../../../alert/AlertContext";
import { ApiErrorCode, getApiErrorCode } from "../../../../../api/apiErrorHelper";
import { withLocalErrorHandling } from "../../../../../api/handleErrorLocally";
import { useFormikWithDirtyForm } from "../../../../../forms/useFormikWithDirtyForm";
import { nameof } from "../../../../../utils/nameof";
import { BalanceEntryRequest, UpdateClaimRequest } from "../../../../api/ClientFileApi";
import { useClaimsApi } from "../../../../api/ClientFileApiContext";
import { ClientFileQueryKey } from "../../../../query/clientFileQueryKeys";
import { ClaimStatusKey } from "../../../claimStatus";
import { ClaimFormValues } from "../../claim-form-fields/ClaimFormFields.types";
import { claimValidationSchema } from "../../claim-form-fields/ClaimFormFields.utils";
import {
  fromCollateralResponses,
  toCollateralRequests,
  toExpectedLossPercent,
} from "../../claim-form-fields/components/collaterals/Collaterals.utils";
import { useClaimFormOptions } from "../../claim-form-fields/hooks/useClaimFormOptions";

export function useClaimEdit(clientId: string, claimId: string, onClose: () => void) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const claimsApi = useClaimsApi();
  const options = useClaimFormOptions();
  const queryClient = useQueryClient();

  const { data: claim, isLoading } = useQuery({
    queryKey: [ClientFileQueryKey.ClaimById, claimId],
    queryFn: () => claimsApi.getClaimById({ clientId, claimId }),
  });

  const successMessageKey = (isCompleted: boolean) => {
    if (isCompleted === claim?.isCompleted) {
      return "claims.edit.successfullyUpdated";
    }
    return isCompleted ? "claims.edit.successfullyCompleted" : "claims.edit.successfullyReopened";
  };

  const updateMutation = useMutation({
    mutationFn: (request: UpdateClaimRequest) =>
      claimsApi.updateClaim({ clientId, claimId, updateClaimRequest: request }, withLocalErrorHandling),
    onSuccess: (_, request) => {
      void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.ClientClaims, clientId] });
      void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.ClaimById, claimId] });
      void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.AssetInventoryAssets, clientId] });
      void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.AssetInventoryCompleteness, clientId] });
      showSuccess(t(successMessageKey(request.isCompleted === true)));
      onClose();
    },
    onError: async (error) => {
      // The save carried the balance list as the form loaded it and the list has moved since, so
      // saving it as it stands would drop whatever was added meanwhile. Nothing was written: the
      // claim is reloaded so the form shows the list that now applies and a second save can go
      // through. The dialog stays open, which the reinitialized form needs to be seen.
      if ((await getApiErrorCode(error)) === ApiErrorCode.ClaimBalanceListChanged) {
        void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.ClientClaims, clientId] });
        void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.ClaimById, claimId] });
        showError(t("claims.edit.balanceListChanged"));
        return;
      }

      showError(t("claims.edit.saveFailed"));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => claimsApi.deleteClaim({ clientId, claimId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [ClientFileQueryKey.ClientClaims, clientId] });
      showSuccess(t("claims.delete.successfullyDeleted"));
      onClose();
    },
  });

  const formik = useFormikWithDirtyForm<ClaimFormValues>({
    enableReinitialize: true,
    initialValues: {
      creditorId: claim?.creditorId ?? "",
      representativeId: claim?.representativeId ?? "",
      caseNumber: claim?.caseNumber ?? "",
      legalBasis: claim?.legalBasis ?? "",
      disputedStatus: claim?.disputedStatus ?? "",
      collateralStatus: claim?.collateralStatus ?? "",
      expectedLossPercent: claim?.expectedLossPercent ?? null,
      collaterals: fromCollateralResponses(claim?.collaterals),
      claimEntirelyDisputed: claim?.claimEntirelyDisputed ?? "",
      recognizedAmount: claim?.recognizedAmount ?? null,
      srvRegistrationStatus: claim?.srvRegistrationStatus ?? "",
      srvOrderNumber: claim?.srvOrderNumber ?? null,
      notes: claim?.notes ?? "",
      isCompleted: claim?.isCompleted ?? false,
      balanceEntries: (claim?.balanceEntries ?? []).map((be) => ({
        id: be.id,
        date: be.date,
        amount: be.amount,
        entryType: be.entryType ?? null,
      })),
    },
    validationSchema: claimValidationSchema(t, options.isSrvRegisteredEntryType, true),
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async (values) => {
      const balanceEntries: BalanceEntryRequest[] = [];
      for (const e of values.balanceEntries) {
        if (e.date && e.amount !== null) {
          balanceEntries.push({ id: e.id ?? null, date: e.date, amount: e.amount, entryType: e.entryType });
        }
      }

      const request: UpdateClaimRequest = {
        representativeId: values.representativeId || null,
        caseNumber: values.caseNumber || null,
        legalBasis: values.legalBasis,
        disputedStatus: values.disputedStatus,
        collateralStatus: values.collateralStatus,
        expectedLossPercent: toExpectedLossPercent(values),
        collaterals: toCollateralRequests(values),
        claimEntirelyDisputed:
          values.disputedStatus === ClaimStatusKey.Yes ? values.claimEntirelyDisputed || null : null,
        recognizedAmount:
          values.disputedStatus === ClaimStatusKey.Yes && values.claimEntirelyDisputed === ClaimStatusKey.No
            ? values.recognizedAmount
            : null,
        srvRegistrationStatus: values.srvRegistrationStatus || null,
        srvOrderNumber: values.srvOrderNumber,
        notes: values.notes || null,
        isCompleted: values.isCompleted,
        balanceEntries,
        // The list the entries above were composed against. The backend refuses the save when it
        // has moved since, rather than letting this payload remove entries the form never saw.
        expectedBalanceListVersionUtc: claim?.balanceListVersionUtc ?? null,
      };

      try {
        await updateMutation.mutateAsync(request);
      } catch {
        // Reported by the mutation's error handler; the dialog stays open so the input is kept.
      }
    },
  });

  const submitWithCompletion = async (isCompleted: boolean) => {
    await formik.setFieldValue(nameof<ClaimFormValues>("isCompleted"), isCompleted);
    await formik.submitForm();
  };

  const handleMarkAsCompleted = () => submitWithCompletion(true);

  const handleMarkAsOpen = () => submitWithCompletion(false);

  const handleDelete = () => {
    deleteMutation.mutate();
  };

  // The representative loaded with the claim is inactive (e.g. its global creditor was
  // deactivated after the claim was created). It stays blocking only while the form still
  // references that inactive representative — picking another active one or clearing the
  // selection resolves it.
  const isInactiveRepresentativeSelected =
    !!claim?.representativeId &&
    claim.isRepresentativeInactive === true &&
    formik.values.representativeId === claim.representativeId;

  return {
    formik,
    options,
    claim,
    isLoading,
    isInactiveRepresentativeSelected,
    handleMarkAsCompleted,
    handleMarkAsOpen,
    handleDelete,
    isDeleting: deleteMutation.isPending,
  };
}
